import React, { useState, useEffect } from 'react';
import { FaTimes, FaUserInjured, FaCalendarAlt, FaFileMedical, FaChartLine } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { appointmentService } from './AppointmentsService';
import axios from 'axios';

const PatientDetailsModal = ({ isOpen, onClose, patientId }) => {
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchPatientDetails = async () => {
      if (!patientId || !isOpen) return;
      
      setLoading(true);
      try {
        // Extract ID if patientId is an object
        const patientIdValue = patientId._id || patientId;
        console.log("Fetching patient details for ID:", patientIdValue);
        
        // Fetch patient data directly from API
        let patientData;
        try {
          const patientResponse = await axios.get(`/api/users/${patientIdValue}`);
          patientData = patientResponse.data;
        } catch (err) {
          console.error("Error fetching patient details:", err);
        }
        
        // Fetch patient's appointments
        const appointments = await appointmentService.getAppointmentsByUser(patientIdValue);
        console.log("Patient appointments:", appointments);
        
        // Create patient info object with the available data
        const patientInfo = {
          id: patientIdValue,
          name: patientData ? `${patientData.firstName} ${patientData.lastName}` : 
                (appointments.length > 0 && appointments[0].patientId && appointments[0].patientId.firstName && appointments[0].patientId.lastName) 
                ? `${appointments[0].patientId.firstName} ${appointments[0].patientId.lastName}` 
                : 'Unknown Patient',
          email: patientData?.email || 'N/A' || (appointments.length > 0 && appointments[0].patientId && appointments[0].patientId.email) ? appointments[0].patientId.email : 'N/A',
          appointments: appointments,
          lastVisit: null,
          upcomingAppointments: [],
          completedAppointments: [],
          vitals: {
            bloodPressure: { value: '120/80', status: 'normal', updatedAt: new Date() },
            heartRate: { value: '72', status: 'normal', updatedAt: new Date() },
            temperature: { value: '98.6', status: 'normal', updatedAt: new Date() },
            oxygenSaturation: { value: '98', status: 'normal', updatedAt: new Date() },
            weight: { value: '160', status: 'normal', updatedAt: new Date() }
          }
        };
        
        // Process appointments
        const now = new Date();
        appointments.forEach(apt => {
          const startTime = new Date(apt.startTime);
          
          if (apt.status === 'completed') {
            patientInfo.completedAppointments.push(apt);
            
            // Find last visit
            if (!patientInfo.lastVisit || startTime > new Date(patientInfo.lastVisit)) {
              patientInfo.lastVisit = apt.startTime;
            }
          }
          
          // Find upcoming appointments
          if (startTime >= now && apt.status !== 'cancelled') {
            patientInfo.upcomingAppointments.push(apt);
          }
        });
        
        // Sort upcoming appointments by date
        patientInfo.upcomingAppointments.sort((a, b) => 
          new Date(a.startTime) - new Date(b.startTime)
        );
        
        setPatient(patientInfo);
      } catch (error) {
        console.error("Error processing patient data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPatientDetails();
  }, [patientId, isOpen]);
  
  if (!isOpen) return null;
  
  // Format date and time from ISO string
  const formatDateTime = (isoString, includeTime = true) => {
    if (!isoString) return 'N/A';
    
    const date = new Date(isoString);
    const dateOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    const timeOptions = { hour: '2-digit', minute: '2-digit' };
    
    return includeTime 
      ? date.toLocaleDateString(undefined, dateOptions) + ' at ' + date.toLocaleTimeString(undefined, timeOptions)
      : date.toLocaleDateString(undefined, dateOptions);
  };
  
  // Map status values to display formats
  const getStatusDisplay = (status) => {
    const statusMap = {
      'scheduled': 'Scheduled',
      'completed': 'Completed',
      'cancelled': 'Cancelled',
      'in-progress': 'In Progress'
    };
    
    return statusMap[status] || status;
  };
  
  // Get status color class
  const getStatusColorClass = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'in-progress':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-blue-600 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-white flex items-center">
            <FaUserInjured className="mr-2" />
            Patient Details
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 focus:outline-none"
          >
            <FaTimes size={24} />
          </button>
        </div>
        
        {/* Content */}
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 68px)' }}>
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
            </div>
          ) : patient ? (
            <div>
              {/* Patient Info */}
              <div className="p-6 border-b">
                <div className="flex items-center">
                  <div className="bg-blue-100 h-16 w-16 rounded-full flex items-center justify-center mr-4">
                    <span className="text-blue-600 font-bold text-xl">
                      {patient.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800">{patient.name}</h3>
                    <p className="text-gray-600 mt-1">
                      {patient.email}
                    </p>
                    <p className="text-gray-500 mt-0.5 text-sm">
                      Patient ID: {patient.id.substring(0, 8)}...
                    </p>
                    <div className="flex items-center mt-1 text-sm text-gray-500">
                      <span className={`h-2 w-2 rounded-full mr-2 
                        ${patient.completedAppointments.length === 0 ? 'bg-green-500' : 'bg-blue-500'}`}>
                      </span>
                      {patient.completedAppointments.length === 0 ? 'New Patient' : 'Regular Patient'}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Tab Navigation */}
              <div className="border-b">
                <nav className="flex">
                  <button
                    className={`px-6 py-3 text-sm font-medium ${
                      activeTab === 'overview' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-blue-600'
                    }`}
                    onClick={() => setActiveTab('overview')}
                  >
                    Overview
                  </button>
                  <button
                    className={`px-6 py-3 text-sm font-medium ${
                      activeTab === 'appointments' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-blue-600'
                    }`}
                    onClick={() => setActiveTab('appointments')}
                  >
                    Appointments
                  </button>
                  <button
                    className={`px-6 py-3 text-sm font-medium ${
                      activeTab === 'vitals' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-blue-600'
                    }`}
                    onClick={() => setActiveTab('vitals')}
                  >
                    Vitals
                  </button>
                </nav>
              </div>
              
              {/* Tab Content */}
              <div className="p-6">
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-500 text-sm mb-1">Total Appointments</p>
                        <p className="text-xl font-semibold">{patient.appointments.length}</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-500 text-sm mb-1">Last Visit</p>
                        <p className="text-xl font-semibold">
                          {patient.lastVisit ? formatDateTime(patient.lastVisit, false) : 'No visits yet'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Next Appointment */}
                    <div>
                      <h4 className="font-medium text-gray-700 mb-2">Next Appointment</h4>
                      {patient.upcomingAppointments.length > 0 ? (
                        <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                          <div className="flex items-center">
                            <FaCalendarAlt className="text-blue-600 mr-3" />
                            <div>
                              <p className="font-medium">
                                {formatDateTime(patient.upcomingAppointments[0].startTime)}
                              </p>
                              <p className="text-sm text-gray-600">
                                {patient.upcomingAppointments[0].title || patient.upcomingAppointments[0].description}
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-500">No upcoming appointments</p>
                      )}
                    </div>
                    
                    {/* Recent Vitals Summary */}
                    <div>
                      <h4 className="font-medium text-gray-700 mb-2">Recent Vitals</h4>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-blue-50 p-3 rounded-lg">
                          <p className="text-xs text-gray-600">Blood Pressure</p>
                          <p className="font-medium mt-1">{patient.vitals.bloodPressure.value}</p>
                        </div>
                        <div className="bg-blue-50 p-3 rounded-lg">
                          <p className="text-xs text-gray-600">Heart Rate</p>
                          <p className="font-medium mt-1">{patient.vitals.heartRate.value} bpm</p>
                        </div>
                        <div className="bg-blue-50 p-3 rounded-lg">
                          <p className="text-xs text-gray-600">Temperature</p>
                          <p className="font-medium mt-1">{patient.vitals.temperature.value} °F</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {activeTab === 'appointments' && (
                  <div>
                    <h4 className="font-medium text-gray-700 mb-3">Appointment History</h4>
                    {patient.appointments.length > 0 ? (
                      <div className="space-y-3 max-h-80 overflow-y-auto">
                        {patient.appointments.map(apt => (
                          <div key={apt._id} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                            <div className="flex justify-between">
                              <div>
                                <p className="font-medium">{formatDateTime(apt.startTime, false)}</p>
                                <p className="text-sm text-gray-600">
                                  {apt.title || apt.description || 'No description'}
                                </p>
                              </div>
                              <span className={`text-xs px-2 py-1 h-fit rounded-full ${getStatusColorClass(apt.status)}`}>
                                {getStatusDisplay(apt.status)}
                              </span>
                            </div>
                            {apt.meetingLink && (
                              <div className="mt-2 text-xs">
                                <a 
                                  href={apt.meetingLink} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="text-blue-600 hover:underline"
                                >
                                  Join Meeting
                                </a>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500">No appointment history available</p>
                    )}
                  </div>
                )}
                
                {activeTab === 'vitals' && (
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-medium text-gray-700">Vital Signs</h4>
                      <button className="text-xs px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors">
                        Update Vitals
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-500 text-sm mb-1">Blood Pressure</p>
                        <div className="flex items-center">
                          <span className={`h-2 w-2 rounded-full mr-2 ${patient.vitals.bloodPressure.status === 'normal' ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                          <p className="text-xl font-semibold">{patient.vitals.bloodPressure.value}</p>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Last updated: {formatDateTime(patient.vitals.bloodPressure.updatedAt, false)}</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-500 text-sm mb-1">Heart Rate</p>
                        <div className="flex items-center">
                          <span className={`h-2 w-2 rounded-full mr-2 ${patient.vitals.heartRate.status === 'normal' ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                          <p className="text-xl font-semibold">{patient.vitals.heartRate.value} bpm</p>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Last updated: {formatDateTime(patient.vitals.heartRate.updatedAt, false)}</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-500 text-sm mb-1">Temperature</p>
                        <div className="flex items-center">
                          <span className={`h-2 w-2 rounded-full mr-2 ${patient.vitals.temperature.status === 'normal' ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                          <p className="text-xl font-semibold">{patient.vitals.temperature.value} °F</p>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Last updated: {formatDateTime(patient.vitals.temperature.updatedAt, false)}</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-500 text-sm mb-1">Oxygen Saturation</p>
                        <div className="flex items-center">
                          <span className={`h-2 w-2 rounded-full mr-2 ${patient.vitals.oxygenSaturation.status === 'normal' ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                          <p className="text-xl font-semibold">{patient.vitals.oxygenSaturation.value}%</p>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Last updated: {formatDateTime(patient.vitals.oxygenSaturation.updatedAt, false)}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="p-6 text-center">
              <p className="text-gray-500">Patient not found</p>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-between items-center border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
          >
            Close
          </button>
          <div>
            <Link
              to={`/patients/details/${patient?.id || patientId}`}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 mr-2"
            >
              Full Profile
            </Link>
            <Link
              to={`/appointments/book?patientId=${patient?.id || patientId}`}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              Schedule Appointment
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientDetailsModal;